<template>
  <TransitionRoot appear :show="show">
    <Dialog
      as="div"
      open
      :initial-focus="initialFocusElement"
      :aria-label="title || ariaLabel || undefined"
      class="fixed top-0 left-0 z-55 h-dvh w-full overflow-y-auto tracking-[0.2px] subpixel-antialiased"
      @close="
        () => {
          if (allowBackdropClose) {
            emit('close')
          }
        }
      "
    >
      <TransitionChild
        as="template"
        enter="duration-200 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay
          class="ds-backdrop fixed top-0 left-0 h-full w-full bg-black/50"
        />
      </TransitionChild>

      <TransitionChild
        as="template"
        enter="duration-200 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="h-dvh overflow-y-auto relative">
          <div
            :class="[
              'flex min-h-dvh justify-center py-2 md:py-10',
              verticalPosition === 'top' ? 'items-start' : 'items-center',
              containerClass,
            ]"
          >
            <div
              ref="panel"
              :class="[
                'relative ml-1 md:ml-0 max-w-[98%] lg:max-w-[1380px] rounded-[10px] bg-white shadow-[0px_6px_19px_0px_rgb(182,182,182,0.35)] ds:rounded-(--ds-radius-xl) ds:border ds:border-(--ds-dialog-border) ds:bg-(--ds-surface-default) ds:shadow-(--ds-elevation-overlay)',
                wrapperClass,
              ]"
              :style="{
                width: !width
                  ? 'auto'
                  : typeof width !== 'number'
                    ? width
                    : width + 'px',
              }"
            >
              <slot v-if="showHeader" name="header">
                <div
                  :class="[
                    'flex items-center rounded-tl-[10px] rounded-tr-[10px] bg-[#f6f7f8] px-4 py-3 md:p-[15px_30px] ds:rounded-t-(--ds-radius-xl) ds:bg-(--ds-surface-default) ds:md:px-[30px] ds:md:pt-6 ds:md:pb-1',
                    headerClass,
                  ]"
                >
                  <h1
                    v-if="title"
                    class="flex-1 text-left text-[15px] font-normal uppercase text-primary ds:text-[18px] ds:font-bold ds:normal-case ds:text-(--ds-text-primary)"
                  >
                    {{ title }}
                  </h1>
                  <span v-else class="flex-1" />
                  <button
                    type="button"
                    aria-label="close icon"
                    class="modal-close-button cursor-pointer"
                    @click="emit('close')"
                  >
                    <FontAwesomeIcon
                      :icon="['fal', 'times']"
                      class="text-[#3d4146] ds:text-[18px] ds:text-(--ds-text-secondary)"
                    />
                  </button>
                </div>
              </slot>

              <!-- The body carries the window's lower corners, and the upper ones too when there
                   is no header: the panel is overflow-visible, so a square-cornered child paints
                   straight over the rounding it would otherwise inherit. -->
              <div
                data-ds-modal-surface
                :class="[
                  'p-4 md:p-[15px_30px] rounded-b-[10px] ds:rounded-b-(--ds-radius-xl)',
                  showHeader
                    ? ''
                    : 'rounded-t-[10px] ds:rounded-t-(--ds-radius-xl)',
                  bodyClass,
                ]"
              >
                <slot />
              </div>
            </div>
          </div>
        </div>
      </TransitionChild>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { computed, onMounted, onUnmounted, useTemplateRef, watch } from 'vue'
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogOverlay,
} from '@headlessui/vue'
import { useHideChatLauncherWhileOpen } from '@/composables/chatLauncher'

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  title: {
    type: [String, null],
    default: null,
  },
  /** Accessible name for modals that render their own heading in the slot
      instead of passing `title` (role="dialog" needs a name either way). */
  ariaLabel: {
    type: [String, null],
    default: null,
  },
  width: {
    type: [Number, String, null],
    default: 600,
  },
  bodyClass: {
    type: String,
    default: '',
  },
  headerClass: {
    type: String,
    default: '',
  },
  containerClass: {
    type: String,
    default: '',
  },
  wrapperClass: {
    type: String,
    default: '',
  },
  verticalPosition: {
    type: String,
    default: 'center',
  },
  showHeader: {
    type: Boolean,
    default: true,
  },
  allowBackdropClose: {
    type: Boolean,
    default: true,
  },
  /**
   * Optional: a ref-like `{ value }` holding the element (or component) to focus on open, for a
   * modal whose content renders its focusable controls only later (headlessui's initialFocus).
   */
  initialFocus: {
    type: Object,
    default: null,
  },
  restoreFocusOnRemoval: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close'])

useHideChatLauncherWhileOpen(() => props.show)

/**
 * headlessui's Dialog takes the element (or component) itself, not a ref: its FocusTrap unwraps
 * only one level, so a ref-like object reached it as „no element“ and the trap fell back to the
 * first control — or, while the content was not rendered yet, warned „There are no focusable
 * elements inside the <FocusTrap />“ (browser check 3, 07.10.). Reading `.value` here also makes
 * the Dialog re-run its initial focus once the element renders.
 */
const initialFocusElement = computed(() => {
  const target = props.initialFocus?.value ?? null

  return target?.$el ?? target
})

const onKeydown = (e) => {
  if (e.key === 'Escape' && props.show) {
    emit('close')
  }
}

const panel = useTemplateRef('panel')

/**
 * A dialog that opens while another one leaves (the trade flyout steps aside for the offer
 * comparison): the leaving dialog's focus trap pulls the focus back for its 200 ms transition and
 * then removes the focused element, so the focus fell to BODY behind this modal (browser check 3,
 * 07.10.). When the focused element is removed while this modal is the topmost dialog, the focus
 * goes to its `initialFocus` or its first control. Focus that moves elsewhere is left alone.
 */
const onFocusout = (event) => {
  if (!props.restoreFocusOnRemoval || !props.show || event.relatedTarget) {
    return
  }

  requestAnimationFrame(() => {
    const element = panel.value
    const dialogs = document.querySelectorAll('[role="dialog"]')

    if (
      !element?.isConnected ||
      event.target.isConnected ||
      document.activeElement !== document.body ||
      !dialogs[dialogs.length - 1]?.contains(element)
    ) {
      return
    }

    const target =
      initialFocusElement.value instanceof HTMLElement &&
      element.contains(initialFocusElement.value)
        ? initialFocusElement.value
        : element.querySelector(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
          )

    target?.focus({ preventScroll: true })
  })
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
  if (props.restoreFocusOnRemoval) {
    document.addEventListener('focusout', onFocusout, true)
  }
})
watch(
  () => props.restoreFocusOnRemoval,
  (enabled) => {
    if (enabled) {
      document.addEventListener('focusout', onFocusout, true)
    } else {
      document.removeEventListener('focusout', onFocusout, true)
    }
  },
)
onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
  document.removeEventListener('focusout', onFocusout, true)
})
</script>
